import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { resolveImageUrl } from '../../utils/imageUrl';
import { buildSubscriberSignInPath, isSubscriberOnlyContent } from '../../utils/subscriberAccess';
import './PostCard.css';

const PostCard = ({ post, variant = 'default', className = '' }) => {
  const { isAuthenticated } = useAuth();
  if (!post) return null;

  // Get the cover image URL for posts
  const getPostImageUrl = (post) => {
    return resolveImageUrl(post.cover_image_url || post.cover_image);
  };

  const postImageUrl = getPostImageUrl(post);

  // Function to strip HTML tags from content
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Use excerpt if available, otherwise truncate body
  const rawDescription = post.excerpt || post.body || '';
  const description = rawDescription 
    ? (stripHtml(rawDescription).length > 150 
        ? `${stripHtml(rawDescription).substring(0, 150)}...`
        : stripHtml(rawDescription))
    : '';

  // Calculate reading time
  const readingTime = post.reading_time || Math.ceil((post.body?.length || 0) / 1000);

  // Format date
  const postDate = new Date(post.published_at || post.created_at);
  const formatDate = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const cardClassName = `post-card ${variant}${post.is_sticky ? ' sticky' : ''}${post.is_featured ? ' featured' : ''} ${className}`.trim();
  const detailPath = `/blog/${post.slug || post.id}`;
  const isLocked = isSubscriberOnlyContent(post);
  const destination =
    isLocked && !isAuthenticated
      ? buildSubscriberSignInPath(detailPath)
      : detailPath;

  return (
    <Link to={destination} className={cardClassName}>
      {/* Post Badges */}
      <div className="post-badges">
        {isLocked && (
          <span className="post-badge featured-badge" title="Premium member content" aria-label="Premium member content">
            <i className="fas fa-crown"></i>
          </span>
        )}
        {post.is_sticky && (
          <span className="post-badge sticky-badge">
            <i className="fas fa-thumbtack"></i>
            Pinned
          </span>
        )}
        {post.is_featured && !post.is_sticky && (
          <span className="post-badge featured-badge">
            <i className="fas fa-star"></i>
            Featured
          </span>
        )}
      </div>

      {/* Post Image */}
      <div className="post-card-image">
        {postImageUrl ? (
          <img 
            src={postImageUrl}
            alt={post.title}
            className="post-cover-image"
            loading="lazy"
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.target.style.display = 'none';
              e.target.parentNode.classList.add('no-image');
            }}
          />
        ) : (
          <div className="post-placeholder-image">
            <i className="fas fa-newspaper"></i>
          </div>
        )}
        <div className="post-image-overlay">
          <div className="reading-time">
            <i className="fas fa-clock"></i>
            {readingTime} min read
          </div>
        </div>
      </div>

      {/* Post Content */}
      <div className="post-card-content">
        {/* Post Meta */}
        <div className="post-card-meta">
          <span className="post-date">{formatDate(postDate)}</span>
          {post.user?.name && <span className="post-author">by {post.user.name}</span>}
          {post.view_count && <span className="post-views">{post.view_count} views</span>}
        </div>

        {/* Post Title */}
        <h3 className="post-card-title">{post.title}</h3>
        
        {/* Post Description */}
        {description && <p className="post-card-description">{description}</p>}

        {/* Post Tags */}
        {post.tags && Array.isArray(post.tags) && post.tags.length > 0 && (
          <div className="post-tags">
            {post.tags.slice(0, 3).map(tag => (
              <span key={tag} className="post-tag">#{tag}</span>
            ))}
            {post.tags.length > 3 && <span className="more-tags">+{post.tags.length - 3}</span>}
          </div>
        )}

        {/* Read More Button */}
        <div className="post-card-footer">
          <div className="post-card-read-btn">
            <span>{isLocked ? 'Sign In to Unlock' : 'Read Article'}</span>
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PostCard;
